import { useEffect, useState } from "react";
import {
    keepPreviousData,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { useStoredPreference } from "@/hooks/useStoredPreference";
import {
    providerFiltersSchema,
    providerSortingSchema,
    providerColumnsSchema,
} from "@/pages/Providers/listState";
import type { ProviderViewState } from "@/pages/Providers/listState";
import type {
    ProviderColumnFilter,
    ProviderPaginationState,
    ProviderSortingState,
} from "@/pages/Providers/types";
import {
    ProviderApiError,
    useProviderApi,
    useProviderContext,
} from "@/pages/Providers/useProviderApi";
import type { ProviderListResponse } from "@/pages/Providers/types";

export const useProviders = () => {
    const { request } = useProviderApi();
    const { token, user, language } = useProviderContext();
    const queryClient = useQueryClient();
    const storageKey = `vigepra_providers:${user.id}:${user.company}`;
    const [search, setSearch] = useStoredPreference(
        `${storageKey}:search`,
        z.string().max(500),
        "",
        true
    );
    const [delayedSearch, setDelayedSearch] = useState(search);
    const [filters, setFilters] = useStoredPreference<Record<string, string>>(
        `${storageKey}:filters`,
        providerFiltersSchema,
        { state: "active", bc: "all", conflicts: "all" },
        true
    );
    const [columnFilters, storeColumnFilters] = useStoredPreference<
        ProviderColumnFilter[]
    >(`${storageKey}:columns`, providerColumnsSchema, [], true);
    const [sorting, storeSorting] = useStoredPreference<ProviderSortingState[]>(
        `${storageKey}:sorting`,
        providerSortingSchema,
        [{ id: "name", desc: false }],
        true
    );
    const [pagination, setPagination] =
        useStoredPreference<ProviderPaginationState>(
            `${storageKey}:pagination`,
            z.object({
                pageIndex: z.number().int().min(0),
                pageSize: z.union([
                    z.literal(20),
                    z.literal(50),
                    z.literal(100),
                ]),
            }),
            { pageIndex: 0, pageSize: 20 },
            true
        );
    const setColumnFilters = (value: ProviderColumnFilter[]): void => {
        storeColumnFilters(value);
        setPagination((previous) => ({ ...previous, pageIndex: 0 }));
    };
    const setSorting = (value: ProviderSortingState[]): void => {
        storeSorting(value);
        setPagination((previous) => ({ ...previous, pageIndex: 0 }));
    };

    useEffect(() => {
        const timeout = window.setTimeout(
            () => setDelayedSearch(search.trim().replace(/\s+/g, " ")),
            300
        );
        return () => window.clearTimeout(timeout);
    }, [search]);

    const query = useQuery({
        queryKey: [
            "providers",
            token,
            user.company,
            language,
            filters,
            delayedSearch,
            columnFilters,
            sorting,
            pagination,
        ],
        queryFn: async ({ signal }): Promise<ProviderListResponse> => {
            const params = new URLSearchParams({
                ...filters,
                search: delayedSearch,
                page: String(pagination.pageIndex + 1),
                per_page: String(pagination.pageSize),
                order: sorting[0]?.id ?? "name",
                direction: sorting[0]?.desc ? "desc" : "asc",
            });
            for (const filter of columnFilters) {
                if (filter.value !== "" && filter.value != null) {
                    params.set(filter.id, String(filter.value));
                }
            }
            return request<ProviderListResponse>(
                `?${params}`,
                undefined,
                signal
            );
        },
        retry: false,
        placeholderData: keepPreviousData,
    });
    const result = query.data ?? null;
    const loading = query.isFetching;
    const error = query.error
        ? query.error instanceof ProviderApiError && query.error.status === 403
            ? "providers.errors.forbidden"
            : "providers.errors.request"
        : null;

    useEffect(() => {
        if (
            result &&
            !query.isPlaceholderData &&
            result.page !== pagination.pageIndex + 1
        ) {
            setPagination((previous) => ({
                ...previous,
                pageIndex: result.page - 1,
            }));
        }
    }, [result, pagination.pageIndex, query.isPlaceholderData]);

    const updateFilter = (name: string, value: string): void => {
        setFilters((previous) => ({ ...previous, [name]: value }));
        setPagination((previous) => ({ ...previous, pageIndex: 0 }));
    };
    const updateSearch = (value: string): void => {
        setSearch(value);
        setPagination((previous) => ({ ...previous, pageIndex: 0 }));
    };
    const clearFilters = (): void => {
        setSearch("");
        setFilters({ state: "active", bc: "all", conflicts: "all" });
        setColumnFilters([]);
        setPagination((previous) => ({ ...previous, pageIndex: 0 }));
    };
    const reviewConflicts = (): void => {
        setFilters({ state: "all", bc: "all", conflicts: "pending" });
        setSearch("");
        setColumnFilters([]);
        setPagination((previous) => ({ ...previous, pageIndex: 0 }));
    };
    const applyView = (view: Omit<ProviderViewState, "preferences">): void => {
        setSearch(view.search);
        setDelayedSearch(view.search.trim());
        setFilters(view.filters);
        setColumnFilters(view.columnFilters);
        setSorting(view.sorting);
        setPagination({ pageIndex: 0, pageSize: view.pageSize });
    };

    return {
        result,
        loading,
        error,
        search,
        filters,
        columnFilters,
        sorting,
        pagination,
        setColumnFilters,
        setSorting,
        setPagination,
        updateFilter,
        updateSearch,
        clearFilters,
        reviewConflicts,
        applyView,
        reload: (): void => {
            void queryClient.invalidateQueries({ queryKey: ["providers"] });
        },
    };
};
